import { DataTable, useColumns } from "@/components/data-table";
import { useFilters } from "@/components/filters";
import { ListPage } from "@/components/list-page";
import { ConfirmDialog, Drawer, ModalHeader, ModalValueExtractor, useOpenModal } from "@/components/modal";
import useHasPermisos from "@/features/auth/hooks/useHasPermisos";
import CsvExportDialog from "@/features/export/components/csv-export-dialog";
import PrintExportDialog from "@/features/export/components/print-dialog-obs";
import XlsxExportDialog from "@/features/export/components/xlsx-export-dialog";
import { AddArticuloMovimientoMutation } from "@/features/inventario/articulo-movimientos/api/add-articulo-movimiento";
import { DeleteArticuloMovimientoMutation } from "@/features/inventario/articulo-movimientos/api/delete-articulo-movimiento";
import { getAllArticuloMovimientos, useGetAllArticuloMovimientosQuery } from "@/features/inventario/articulo-movimientos/api/get-all-articulo-movimientos";
import ArticuloMovimientoForm, { articuloMovimientoFormSchema } from "@/features/inventario/articulo-movimientos/components/articulo-movimiento-form";
import { formatDate, formatNumber, formatString } from "@/shared/formats";
import { notify } from "@/shared/notification";
import { Permiso } from "@/shared/types/permiso";
import { ArticuloMovimiento, ArticuloMovimientoTipo } from "@/shared/types/articulo-movimiento";
import { SchemaFormHook } from "@/components/form";

export default function ArticuloMovimientosPage() {
    const openModal = useOpenModal();

    const hasWritePermiso = useHasPermisos(Permiso.WriteArticuloMovimientos);

    const filterData = useFilters({
        filters: [
            { type: "dateRange", label: "Fecha", fromName: "desde", toName: "hasta" },
            {
                type: "select", label: "Tipo", name: "tipo", options: [
                    { label: "Entrada", value: ArticuloMovimientoTipo.Entrada },
                    { label: "Salida", value: ArticuloMovimientoTipo.Salida }
                ]
            },
            { type: "pagination" }
        ]
    });

    const fetchData = useGetAllArticuloMovimientosQuery(filterData.filterData);

    const columnData = useColumns<ArticuloMovimiento>({
        columns: [
            { key: "fecha", header: "Fecha", render: row => formatDate(row.fecha), width: "auto" },
            { key: "articulo", header: "Artículo", render: row => formatString(row.articuloNombre) },
            { key: "tipo", header: "Tipo", render: row => row.tipo === ArticuloMovimientoTipo.Entrada ? "Entrada" : "Salida" },
            { key: "descripcion", header: "Motivo", render: row => formatString(row.descripcion) },
            { key: "cantidad", header: "Cantidad", render: row => formatNumber(row.cantidad), align: "right", width: "auto" },
            { key: "creadoPor", header: "Creado Por", render: row => formatString(row.creadoPor), show: false },
            { key: "creacion", header: "Fecha de Creación", render: row => formatDate(row.fechaCreacion), align: "center", show: false },
            { key: "modificadoPor", header: "Modificado Por", render: row => formatString(row.modificadoPor), show: false },
            { key: "modificacion", header: "Última Modificación", render: row => formatDate(row.fechaModificacion), align: "center", show: false },
        ],
        key: "articulo-movimientos"
    });

    return <ListPage>
        <ListPage.Header>
            <ListPage.Header.Actions title="Movimientos de Artículo">
                <ListPage.Header.Actions.FilterButton />
                <ListPage.Header.Actions.ColumnButton columnData={columnData} />
                {hasWritePermiso && <ListPage.Header.Actions.AddButton onClick={() => openModal("add")} />}
                <ListPage.Header.Actions.Divider />
                <ListPage.Header.Actions.ExportButton onClick={() => openModal("print")}>
                    <ListPage.Header.Actions.ExportButton.Print onClick={() => openModal("print")} />
                    <ListPage.Header.Actions.ExportButton.Csv onClick={() => openModal("csv")} />
                    <ListPage.Header.Actions.ExportButton.Xlsx onClick={() => openModal("xlsx")} />
                </ListPage.Header.Actions.ExportButton>
            </ListPage.Header.Actions>
            <ListPage.Header.Filters filterData={filterData} />
        </ListPage.Header>
        <DataTable flex={1}>
            <DataTable.Content
                rows={fetchData.data?.result}
                columns={[
                    ...columnData.columns,
                    hasWritePermiso && {
                        header: "Acción", width: "auto", align: "center", render: row => <DataTable.EntryOption>
                            <DataTable.EntryOption.DeleteButton disabled={row.protegido} title="Anular" onClick={() => openModal("delete", row)} />
                        </DataTable.EntryOption>
                    }
                ]}
                fit="flex"
                loading={fetchData.isFetching}
                error={fetchData.error}
            />
            <DataTable.Pagination filterData={filterData} queryResult={fetchData} />
        </DataTable>

        <Drawer code="add">
            <ModalHeader>Agregar Nuevo Movimiento</ModalHeader>
            <AddArticuloMovimientoMutation>
                {mutation => <SchemaFormHook
                    schema={articuloMovimientoFormSchema}
                    onSubmit={async ({ value }) => {
                        await mutation.mutateAsync(value);
                        notify("La movimiento se ha agregado correctamente.");
                    }}
                    closeModalOnSubmit
                    submitOnce
                >
                    {form => <ArticuloMovimientoForm form={form} margin={3} flex={1} />}
                </SchemaFormHook>}
            </AddArticuloMovimientoMutation>
        </Drawer>

        <ConfirmDialog code="delete">
            <ModalValueExtractor>
                {articuloMovimiento => <DeleteArticuloMovimientoMutation id={articuloMovimiento.id}>
                    {mutation => <ConfirmDialog.Content
                        title={`Anular Movimiento ${formatDate(articuloMovimiento.fecha)}`}
                        description={`¿Estás seguro que quieres anular el movimiento ${formatDate(articuloMovimiento.fecha)}?`}
                        onConfirm={async () => {
                            await mutation.mutateAsync({});
                            notify(`El movimiento "${formatDate(articuloMovimiento.fecha)}" se ha anulado correctamente.`, { variant: "success" });
                        }}
                    />}
                </DeleteArticuloMovimientoMutation>}
            </ModalValueExtractor>
        </ConfirmDialog>

        <PrintExportDialog
            code="print"
            columns={columnData.columns}
            filterData={filterData}
            fetchFn={getAllArticuloMovimientos}
            defaultFileTitle="Movimientos de Artículos"
        />

        <CsvExportDialog
            code="csv"
            columns={columnData.columns}
            filterData={filterData}
            fetchFn={getAllArticuloMovimientos}
            defaultFileTitle="Movimientos de Artículos"
        />

        <XlsxExportDialog
            code="xlsx"
            columns={columnData.columns}
            filterData={filterData}
            fetchFn={getAllArticuloMovimientos}
            defaultFileTitle="Movimientos de Artículos"
        />
    </ListPage>
}